import React, { useState, useMemo } from 'react';
import {
  FileText, Sparkles, Mic, Lock, ShieldCheck, Plus, ChevronDown,
  Layers, Stethoscope, CheckCircle2, XCircle, Calendar, Camera,
  Crown, ArrowRight
} from 'lucide-react';

/* DHF Composer Mock — production-faithful visual language.
   Matches HealthFileSection.js styles verbatim:
   - Outer sky wrapper, framed icon head-boxes
   - 9px badges, 10px eyebrows, 11-12px body — dense
   - Default Inter, Outfit only on H1 title
   - Correction wrapped in: tabbed Simple/Multi leaves.
*/

const OUTFIT = { fontFamily: 'Outfit, sans-serif' };

/* Chips — production-exact sizes */
const EmeraldChip = ({ children, className = '' }) => (
  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 ${className}`}>{children}</span>
);
const EmeraldFilledChip = ({ children }) => (
  <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-full">{children}</span>
);
const AmberChip = ({ children }) => (
  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">{children}</span>
);
const SkyPill = ({ children, testid }) => (
  <span data-testid={testid} className="text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">
    <Lock size={8} /> {children}
  </span>
);
const EmeraldPillPPD = () => (
  <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">
    <Lock size={8} /> PpD
  </span>
);

const SectionLabel = ({ children, className = '', color = 'slate' }) => {
  const map = { slate: 'text-slate-700', rose: 'text-rose-800', amber: 'text-amber-800' };
  return <p className={`text-[10px] font-bold uppercase tracking-wider ${map[color]} ${className}`}>{children}</p>;
};

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{children}</p>
);

/* Textarea + input styles matching production */
const inputCls = "w-full h-9 border border-slate-200 rounded-lg px-3 text-[13px] bg-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 placeholder-slate-400";
const areaCls  = "w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px] leading-relaxed bg-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 resize-y placeholder-slate-400";

/* ─── Follow-up plan with inline date suggestion ─────────────────── */
const FollowupPlan = () => {
  const [text, setText] = useState('');
  const suggested = useMemo(() => {
    const m = text.match(/(\d+)\s*days/i);
    if (!m) return null;
    const d = new Date();
    d.setDate(d.getDate() + parseInt(m[1], 10));
    return d.toISOString().slice(0, 10);
  }, [text]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <SectionLabel>Follow-up plan</SectionLabel>
        <SkyPill>DpD</SkyPill>
      </div>
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Review after 7 days · Repeat tests on day 14" className={inputCls} />
      {suggested && (
        <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50">
          <Calendar size={12} className="text-emerald-700" />
          <span className="text-[10.5px] font-semibold text-emerald-800 uppercase tracking-wider">Suggested follow-up date</span>
          <input type="date" defaultValue={suggested} className="h-6 text-[11.5px] rounded border border-emerald-200 bg-white px-1.5 text-emerald-900" />
          <button className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-800">Confirm</button>
        </div>
      )}
    </div>
  );
};

/* ─── Instructions section ───────────────────────────────────────── */
const InstructionsSection = ({ tone, label, placeholder }) => {
  const T = tone === 'green'
    ? { Icon: CheckCircle2, ico: 'text-emerald-600', num: 'bg-emerald-500' }
    : { Icon: XCircle,       ico: 'text-rose-600',    num: 'bg-rose-500' };
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <T.Icon size={12} className={T.ico} />
        <SectionLabel>{label}</SectionLabel>
        <SkyPill>DpD</SkyPill>
        <button className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 flex items-center gap-0.5"><Plus size={10} /> Add</button>
      </div>
      <div className="flex items-center gap-2">
        <span className={`w-5 h-5 rounded-full ${T.num} text-white flex items-center justify-center text-[9.5px] font-bold shrink-0`}>1</span>
        <input placeholder={placeholder} className={inputCls} />
      </div>
    </div>
  );
};

/* ─── Simple leaf ─────────────────────────────────────────────────── */
const SimpleLeaf = () => (
  <div className="space-y-4">
    {/* Doctor's Assessment */}
    <div>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <SectionLabel>
          <span className="text-teal-700 mr-1.5">Simple Medical Condition :</span>
          Doctor&rsquo;s Assessment <span className="normal-case font-normal text-slate-500 tracking-normal text-[10px] ml-1">(first line = diagnosis)</span>
        </SectionLabel>
        <SkyPill>DpD</SkyPill>
      </div>
      <textarea rows={4} defaultValue={"Viral upper respiratory infection\nNo signs of bacterial infection\nCondition is mild and self-limiting"} className={areaCls} />
    </div>

    {/* Tests + Red flags */}
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <SectionLabel>Tests advised <span className="normal-case font-normal text-slate-500 tracking-normal text-[10px] ml-1">(one per line)</span></SectionLabel>
          <SkyPill>DpD</SkyPill>
        </div>
        <textarea rows={4} defaultValue={"CBC\nLipid profile"} className={areaCls} />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <SectionLabel>Red flags · when to seek help <span className="normal-case font-normal text-slate-500 tracking-normal text-[10px] ml-1">(one per line)</span></SectionLabel>
          <SkyPill>DpD</SkyPill>
        </div>
        <textarea rows={4} defaultValue={"Fever > 102°F\nWorsening pain"} className={areaCls} />
      </div>
    </div>

    <FollowupPlan />

    <div className="grid grid-cols-2 gap-3">
      <InstructionsSection tone="green" label="Instructions : Do&rsquo;s"    placeholder="Drink plenty of warm fluids and rest" />
      <InstructionsSection tone="rose"  label="Instructions : Don&rsquo;ts"  placeholder="Avoid cold drinks and outdoor exposure" />
    </div>
  </div>
);

/* ─── Multi Threaded leaf — production-exact Health Thread box +
       Do's/Don'ts added inside the thread only ────────────────────── */
const ThreadCard = () => (
  <div className="rounded-xl border-2 border-blue-300 bg-white overflow-hidden">
    {/* Blue header bar */}
    <div className="bg-blue-600 text-white px-3 py-2 flex items-center gap-2">
      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold shrink-0">1</span>
      <input
        placeholder="Health thread name (e.g. Viral URI, Hypertension)"
        className="flex-1 min-w-0 rounded-md px-2 py-1 bg-white/15 border border-white/30 text-white placeholder-white/70 text-sm font-semibold"
      />
      <select className="text-[9px] font-bold uppercase tracking-wider bg-white/20 border border-white/40 rounded-full px-2 py-0.5 text-white">
        <option>🟢 OPEN</option><option>🟡 IN PROGRESS</option><option>✅ CURED</option><option>🔒 CONCLUDED</option>
      </select>
      <span className="text-[9px] font-bold uppercase tracking-wider bg-white/20 border border-white/40 rounded-full px-2 py-0.5 flex items-center gap-1"><Lock size={8} /> DpD</span>
      <button className="text-white/85 hover:text-white text-lg leading-none px-1">×</button>
    </div>

    {/* Body */}
    <div className="p-3 space-y-3">
      {/* Related symptoms */}
      <div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Related symptoms</p>
        <div className="flex items-center gap-1.5">
          <input placeholder="+ Add symptom" className="px-2 py-0.5 rounded-full border border-slate-200 bg-white text-[11px] text-slate-700 w-32 outline-none" />
          <button className="w-6 h-6 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"><Plus size={11} /></button>
        </div>
      </div>

      {/* Assessment note */}
      <div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Assessment note (this condition only)</p>
        <textarea rows={2} placeholder="e.g. No exudate on exam. Likely self-limiting." className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 resize-y placeholder-slate-400" />
      </div>

      {/* OPD procedure done + notes */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">OPD procedure done this visit</p>
          <input placeholder="e.g. Suture removal · I&D · Joint aspiration" className="w-full h-8 border border-slate-200 rounded-lg px-2 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 placeholder-slate-400" />
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">OPD procedure notes</p>
          <input placeholder="Brief · site, tolerance, dressing" className="w-full h-8 border border-slate-200 rounded-lg px-2 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 placeholder-slate-400" />
        </div>
      </div>

      {/* Tests advised + Red flags */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tests advised</p>
          <textarea rows={2} placeholder="Optional · one per line" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 resize-y placeholder-slate-400" />
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Red flags · when to seek help</p>
          <textarea rows={2} placeholder="Optional · one per line" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 resize-y placeholder-slate-400" />
        </div>
      </div>

      {/* Follow-up */}
      <div>
        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Follow-up</p>
        <input placeholder="e.g. Review after 5 days" className="w-full h-8 border border-slate-200 rounded-lg px-2 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 placeholder-slate-400" />
      </div>

      {/* Expected improvement milestones */}
      <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px]">🎯</span>
          <p className="text-[10px] font-bold uppercase tracking-wider text-violet-800 flex-1">Expected improvement milestones</p>
          <button className="text-[10px] font-bold uppercase tracking-wider text-violet-700 hover:text-violet-800 flex items-center gap-0.5"><Plus size={10} /> Add</button>
        </div>
        <p className="text-[11px] italic text-slate-500 mt-1">Optional. Set 1-4 improvement targets — each medicine below can tick these as &ldquo;contributes to&rdquo;.</p>
      </div>

      {/* NEW · Do's + Don'ts inside the thread */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle2 size={11} className="text-emerald-600" />
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Instructions : Do&rsquo;s <span className="normal-case font-normal text-slate-400 tracking-normal">(this condition)</span></p>
            <button className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 flex items-center gap-0.5"><Plus size={10} /> Add</button>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[9.5px] font-bold shrink-0">1</span>
            <input placeholder="Quit smoking gradually with support" className="flex-1 h-8 border border-slate-200 rounded-lg px-2 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 placeholder-slate-400" />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <XCircle size={11} className="text-rose-600" />
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Instructions : Don&rsquo;ts <span className="normal-case font-normal text-slate-400 tracking-normal">(this condition)</span></p>
            <button className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 flex items-center gap-0.5"><Plus size={10} /> Add</button>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center text-[9.5px] font-bold shrink-0">1</span>
            <input placeholder="Avoid known seasonal triggers" className="flex-1 h-8 border border-slate-200 rounded-lg px-2 text-[12px] outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 placeholder-slate-400" />
          </div>
        </div>
      </div>

      {/* Medicines linked */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2 flex items-center justify-between">
        <span className="text-[11.5px] text-slate-700">💊 Medicines linked to this condition · <b className="text-slate-900">0</b></span>
        <span className="text-[10.5px] italic text-slate-500">Scroll down and add a medicine — set the &ldquo;for&rdquo; field to this condition.</span>
      </div>

      {/* Progress note */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-2">
        <p className="text-[11px] italic text-slate-500">Progress chip &amp; doctor&rsquo;s observation unlock on the next visit — this is a fresh thread with nothing to compare against yet.</p>
      </div>
    </div>
  </div>
);

const MultiLeaf = () => (
  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <SectionLabel>Health Threads · Longitudinal Case Files</SectionLabel>
      <EmeraldFilledChip>NEW · OPTIONAL</EmeraldFilledChip>
      <div className="ml-auto"><SkyPill>DpD</SkyPill></div>
    </div>
    <p className="text-[11.5px] text-slate-600 mb-3 leading-relaxed">
      Open a thread to have DocSite track this issue <b className="text-slate-800">across every future visit</b> until you mark it cured or concluded.
      Each thread owns its own symptoms · tests · red-flags · follow-up. Medicines below can be tagged to a thread. Skip this section to stay with the flat, one-visit flow.
    </p>

    <ThreadCard />

    <button className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 text-[11px] font-bold uppercase tracking-wider">
      <Plus size={12} /> Open new health thread
    </button>
  </div>
);

/* ═════════════════════════════════════════════════════════════════ */
export default function DhfComposerMock() {
  const [tab, setTab] = useState('simple');

  return (
    <div className="min-h-screen bg-slate-50 py-6 font-sans text-slate-900">
      <div className="max-w-[1080px] mx-auto">
        {/* Production-exact outer container */}
        <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50/40 to-white p-4">

          {/* HEADER — matches production line-for-line */}
          <div className="flex items-start gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-sky-200 flex items-center justify-center shrink-0">
              <FileText size={18} className="text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-slate-900" style={OUTFIT}>Digital Health File</p>
                <EmeraldChip>Patient-Owned</EmeraldChip>
                <AmberChip><Crown size={9} /> Turbo Pass · Free</AmberChip>
              </div>
              <p className="text-[11px] text-slate-600 mt-1">Fill the clinical body below, then generate a sealed file the patient receives instantly.</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold text-emerald-700">
                  Arundhati Suryakant Ardekar · used 0 of 3 free files (DocSite-wide)
                </span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Free quota</p>
              <p className="text-sm font-bold text-slate-900 tabular-nums">3/3</p>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-[11px] font-bold uppercase tracking-wider">
              <Sparkles size={11} className="text-slate-500" /> Pull last DHF for this patient
            </button>
            <button className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-[11px] font-bold uppercase tracking-wider">
              <Mic size={11} className="text-slate-500" /> Voice mode
              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5">3 FREE</span>
            </button>
          </div>

          {/* Patient Baseline — amber block */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-3 mb-3">
            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
              <SectionLabel color="amber">Patient Baseline · Past + Ongoing Conditions &amp; Surgeries</SectionLabel>
              <EmeraldFilledChip>FIRST VISIT · CAPTURE NOW</EmeraldFilledChip>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11.5px] font-semibold text-slate-700">1 · Baseline Health Conditions</span>
                <button className="text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-800 flex items-center gap-0.5"><Plus size={10} /> Add</button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11.5px] font-semibold text-slate-700">2 · Past Surgeries</span>
                <button className="text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-800 flex items-center gap-0.5"><Plus size={10} /> Add</button>
              </div>
            </div>
          </div>

          {/* Past Allergies — rose block */}
          <div className="rounded-2xl border border-rose-200 bg-rose-50/30 p-3 mb-3">
            <div className="flex items-center gap-1.5 mb-2">
              <ShieldCheck size={12} className="text-rose-600" />
              <SectionLabel color="rose">Past Allergies</SectionLabel>
              <div className="ml-auto"><SkyPill>DpD</SkyPill></div>
            </div>
            <div className="flex items-center gap-2">
              <input placeholder="+ Add allergy" className={`${inputCls} flex-1`} />
              <div className="relative">
                <select className="h-9 border border-slate-200 rounded-lg px-3 pr-7 text-[12px] bg-white appearance-none outline-none w-28"><option>Mild</option><option>Moderate</option><option>Severe</option></select>
                <ChevronDown className="absolute right-2 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
              <button className="w-9 h-9 border border-slate-200 rounded-lg bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"><Plus size={13} /></button>
            </div>
          </div>

          {/* Reason for visit */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1.5">
              <SectionLabel>Reason for visit</SectionLabel>
              <EmeraldPillPPD />
            </div>
            <textarea rows={2} defaultValue="Ear pain" className={areaCls} />
          </div>

          {/* Age + Consultation */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <SectionLabel>Patient age</SectionLabel>
                <EmeraldPillPPD />
              </div>
              <input defaultValue="80" className={`${inputCls} bg-slate-50/70`} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <SectionLabel>Consultation type</SectionLabel>
                <SkyPill>DpD</SkyPill>
              </div>
              <div className="relative">
                <select className={`${inputCls} pr-8 appearance-none`}><option>In-person consultation</option><option>Video consultation</option></select>
                <ChevronDown className="absolute right-2.5 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* ─── TABBED BODY ─── */}
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl border border-slate-200 bg-slate-100/60 mb-3">
              <button onClick={() => setTab('simple')} className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition ${
                tab === 'simple' ? 'bg-white text-teal-700 shadow-sm border border-teal-200' : 'text-slate-500 hover:text-slate-700'
              }`}>
                <Stethoscope size={13} />
                Simple Medical Condition
                <span className="normal-case font-normal text-slate-400 tracking-normal text-[10px]">(single-visit)</span>
              </button>
              <button onClick={() => setTab('multi')} className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition ${
                tab === 'multi' ? 'bg-white text-emerald-700 shadow-sm border border-emerald-200' : 'text-slate-500 hover:text-slate-700'
              }`}>
                <Layers size={13} />
                Multi Threaded Medical Condition
                <span className="normal-case font-normal text-slate-400 tracking-normal text-[10px]">(longitudinal)</span>
              </button>
            </div>
            <div>{tab === 'simple' ? <SimpleLeaf /> : <MultiLeaf />}</div>
          </div>

          {/* Medicines */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <SectionLabel>Medicines Prescribed</SectionLabel>
              <SkyPill>DpD</SkyPill>
            </div>
            <button className="text-[11px] font-bold uppercase tracking-wider text-sky-700 hover:text-sky-800 flex items-center gap-1"><Plus size={12} /> Add medicine</button>
          </div>

          {/* Handwritten Rx · turbo */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/30 p-3 mb-3">
            <div className="flex items-center gap-2 mb-1.5">
              <SectionLabel>Handwritten Prescription Photo · Turbo</SectionLabel>
            </div>
            <p className="text-[11.5px] text-slate-600 mb-2 flex items-start gap-1">
              <Crown size={11} className="text-amber-600 mt-0.5" />
              <span><b>Only 1 page allowed</b> — we&rsquo;re building the rest inside HealthifyX. Image auto-compresses on your device before upload (WhatsApp-style).</span>
            </p>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-b from-amber-400 to-amber-500 text-white hover:from-amber-500 hover:to-amber-600 text-[11px] font-bold uppercase tracking-wider">
              <Camera size={12} /> Take / upload 1 photo
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="text-[10.5px] text-slate-500 flex items-center gap-1"><Lock size={10} /> Records cannot be deleted — edit notes/source only.</div>
            <div className="flex items-center gap-2">
              <button className="text-[11.5px] font-medium px-3 py-1.5 rounded-lg text-slate-700 hover:bg-white">Cancel</button>
              <button className="text-[11.5px] font-bold uppercase tracking-wider px-4 py-2 rounded-lg bg-gradient-to-b from-sky-500 to-sky-600 text-white hover:from-sky-600 hover:to-sky-700 shadow-[0_4px_14px_-4px_rgba(2,132,199,0.5)] flex items-center gap-1.5">
                <ArrowRight size={13} /> Draft Save · Clinical Data
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
